import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";

const TYPES = [
  { id: "general", label: "General" },
  { id: "guest", label: "Guests" },
  { id: "media", label: "Media" },
  { id: "sponsor", label: "Sponsors" },
];

export default function Contact() {
  const [type, setType] = useState("general");
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", { ...form, type });
      toast.success("Message sent. We'll be in touch.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-28" data-testid="contact-page">
      <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold mb-4">Get In Touch</p>
      <h1 className="font-display text-5xl md:text-7xl font-black tracking-tighter">
        Send us a <span className="text-svn-red">signal.</span>
      </h1>
      <p className="mt-6 text-lg text-neutral-300 max-w-2xl font-body">
        Guest pitches, media requests, sponsorship inquiries, or just a
        message — pick a lane and we'll route it to the right team.
      </p>

      <Tabs value={type} onValueChange={setType} className="mt-12" data-testid="contact-tabs">
        <TabsList className="bg-black border border-white/10 rounded-none p-1">
          {TYPES.map((t) => (
            <TabsTrigger
              key={t.id}
              value={t.id}
              data-testid={`contact-tab-${t.id}`}
              className="rounded-none px-5 py-2 data-[state=active]:bg-svn-red data-[state=active]:text-white text-neutral-300 font-bold uppercase tracking-widest text-xs"
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {TYPES.map((t) => (
          <TabsContent key={t.id} value={t.id}>
            <form onSubmit={submit} className="mt-8 space-y-4 bg-[#0F0F0F] border border-white/10 p-8 max-w-2xl" data-testid={`contact-form-${t.id}`}>
              <Field testId="contact-name" label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
              <Field testId="contact-email" type="email" label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
              <Field testId="contact-subject" label="Subject" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} />
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-400">Message</label>
                <textarea
                  required
                  rows={6}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="contact-message"
                  className="mt-2 w-full bg-black border border-white/15 px-4 py-3 text-white focus:outline-none focus:border-gold"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-btn"
                className="bg-svn-red text-white font-black uppercase tracking-widest text-sm px-8 py-4 hover:bg-red-700 transition disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Send Message"}
              </button>
            </form>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId, required }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-400">{label}</label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-2 w-full bg-black border border-white/15 px-4 py-3 text-white focus:outline-none focus:border-gold"
      />
    </div>
  );
}
